import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, HeartHandshake, ShieldCheck, Check, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ConsultCTA } from "@/components/brand/ConsultCTA";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { cn } from "@/lib/utils";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/membership")({
  head: () =>
    seo({
      title: "Membership · Currency Thoughts",
      description:
        "How we walk with families at every stage of their legacy journey — from the Currency Thoughts Store to the Family Command Center.",
      ogImage: "https://currencythoughts.net/og-seal.png",
      twitterCard: "summary_large_image",
      canonical: "/membership",
    }),
  component: MembershipPage,
});

function MembershipPage() {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* SECTION 1 — Hero */}
        <section className="bg-navy-900">
          <FiligreeFrame intensity="medium" className="py-28 px-6">
            <div className="mx-auto max-w-4xl text-center">
              <div className="flex justify-center">
                <Eyebrow color="bronze">How We Walk With Families</Eyebrow>
              </div>
              <h1
                className="mt-6 font-display font-semibold text-cream-50"
                style={{ fontSize: "clamp(36px,5vw,56px)", lineHeight: 1.1, letterSpacing: "-0.01em" }}
              >
                Three ways to{" "}
                <em className="italic text-bronze-400 font-normal">walk with us.</em>
              </h1>
              <p className="mx-auto mt-7 max-w-[600px] font-sans text-[17px] leading-[1.65] text-cream-50/85">
                Currency Thoughts serves families at every stage of their legacy
                journey. Where you start isn't where you end — many families
                begin in our Library and grow into the Family Command Center as
                their planning matures.
              </p>
            </div>
          </FiligreeFrame>
        </section>

        {/* SECTION 2 — Tier Cards */}
        <section className="bg-cream-50 py-24 px-6">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-6 md:grid-cols-3">
            {/* CARD 1 — Library Member */}
            <TierCard topBorder="border-t-[3px] border-t-bronze-400">
              <SealRing size={56}>
                <BookOpen className="h-6 w-6 text-bronze-600" strokeWidth={1.5} />
              </SealRing>
              <TierName>Library Member</TierName>
              <TierTag className="text-charcoal-500">
                For families learning the foundations of stewardship.
              </TierTag>
              <Divider />
              <AccessList
                items={[
                  "The Academy — public learning tracks, the Glossary, and Ask Currency Thoughts",
                  "The Legacy Score and Family Snapshot",
                  "The Family Library — free workbooks and checklists",
                  "Browse and purchase from the Store",
                ]}
              />
              <div className="mt-auto pt-8">
                <SecondaryButton
                  asChild
                  className="w-full border-bronze-600 text-navy-800 hover:bg-bronze-400 hover:text-navy-900"
                >
                  <Link to="/academy/library">Browse the Family Library →</Link>
                </SecondaryButton>
              </div>
            </TierCard>

            {/* CARD 2 — Family Member */}
            <TierCard topBorder="border-t-[3px] border-t-navy-800">
              <SealFilled size={56} fill="bg-navy-800">
                <HeartHandshake className="h-6 w-6 text-bronze-400" strokeWidth={1.5} />
              </SealFilled>
              <TierName>Family Member</TierName>
              <TierTag className="text-charcoal-500">
                For families starting their protection foundation.
              </TierTag>
              <Divider />
              <AccessList
                items={[
                  "Everything in Library Member",
                  "Full Academy access — all client learning tracks and exclusive lessons",
                  "Your policies and coverage overview",
                  "Private document vault (secure upload)",
                  "Booked appointments with your strategist",
                  "All financial calculators (full set)",
                  "Browse and purchase from the Store",
                ]}
              />
              <div className="mt-auto pt-8">
                <ConsultCTA variant="primary" size="default" className="w-full" />
              </div>
            </TierCard>

            {/* CARD 3 — Legacy Member */}
            <TierCard
              topBorder="border-t-[4px] border-t-bronze-600"
              className="relative shadow-[0_10px_40px_-12px_rgba(168,139,92,0.35)]"
            >
              {/* Floating Premier pill */}
              <div className="absolute right-6 top-0 -translate-y-1/2">
                <span
                  className="inline-block bg-bronze-600 px-[10px] py-1 font-sans text-[9px] font-bold uppercase tracking-[0.18em] text-navy-800"
                  style={{ borderRadius: "2px" }}
                >
                  Premier
                </span>
              </div>

              <SealLegacy size={62}>
                <ShieldCheck className="h-6 w-6 text-bronze-400" strokeWidth={1.5} />
              </SealLegacy>
              <TierName className="font-bold">Legacy Member</TierName>
              <TierTag className="font-medium text-bronze-600">
                For families building substantial, generational protection.
              </TierTag>
              <hr className="my-4 border-0 border-t border-bronze-600/60" />
              <AccessList
                items={[
                  "Everything in Family Member",
                  <><strong className="font-semibold text-navy-800">The Family Command Center</strong></>,
                  "Secure messaging with your strategist",
                  "Family planning checklists and goals tracker",
                  "Family member access (spouse + dependents)",
                  "Annual review automation",
                  "Beneficiary audit reminders",
                  "Estate document secure-share",
                  "Annual Family Statement (year-end PDF)",
                  "Priority support",
                ]}
              />
              <div className="mt-auto pt-8">
                <ConsultCTA
                  size="default"
                  className="w-full bg-navy-800 text-cream-50 hover:bg-navy-700 hover:text-cream-50"
                />
              </div>
            </TierCard>
          </div>
        </section>

        {/* Sections 3-5 in next prompt */}

        {/* SECTION 3 — How Families Qualify */}
        <section className="bg-white py-20 px-6">
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex justify-center">
              <Eyebrow color="bronze">Transparent Qualification</Eyebrow>
            </div>
            <h2 className="mt-6 font-display text-[36px] font-semibold leading-tight text-navy-800">
              How families reach{" "}
              <em className="italic font-normal text-bronze-600">Legacy Member.</em>
            </h2>
          </div>

          <div className="mx-auto mt-10 max-w-[720px]">
            <p
              className="font-display text-[18px] leading-[1.7] text-charcoal-900"
              style={{ textAlign: "left" }}
            >
              <span className="float-left mr-3 mt-1 font-display text-[56px] font-bold leading-[0.85] text-bronze-600">
                L
              </span>
              egacy Member is granted by a strategist, by name, with a written
              reason recorded. It is not automatic, and it cannot be purchased.
              There is no threshold to hit and no score to reach. A strategist
              who knows your family decides, documents why, and that record
              stays on file. It is a depth-of-relationship marker, not a paywall.
            </p>
          </div>

          <div className="mx-auto mt-10 grid max-w-[720px] grid-cols-1 gap-3 sm:grid-cols-3">
            <QualifierCard
              title="Human"
              subtitle="A Strategist Decides"
            />
            <QualifierCard
              title="Named"
              subtitle="Granted By A Person"
            />
            <QualifierCard
              title="Recorded"
              subtitle="Written Reason On File"
              compact
            />
          </div>
        </section>

        {/* SECTION 4 — The Journey */}
        <section className="bg-cream-50 py-20 px-6">
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex justify-center">
              <Eyebrow color="bronze">The Journey</Eyebrow>
            </div>
            <h2 className="mt-6 font-display text-[36px] font-semibold leading-tight text-navy-800">
              Where you start isn't{" "}
              <em className="italic font-normal text-bronze-600">where you end.</em>
            </h2>
          </div>

          <div className="mx-auto mt-12 flex max-w-3xl flex-col items-center justify-center gap-6 md:flex-row md:gap-4">
            <JourneyStep
              icon={<BookOpen className="h-4 w-4 text-bronze-600" strokeWidth={1.75} />}
              label="Library Member"
              sublabel="Discover and learn"
              ringClass="border-2 border-bronze-400 bg-transparent"
            />
            <JourneyArrow />
            <JourneyStep
              icon={<HeartHandshake className="h-4 w-4 text-bronze-400" strokeWidth={1.75} />}
              label="Family Member"
              sublabel="Begin the relationship"
              ringClass="bg-navy-800"
            />
            <JourneyArrow />
            <JourneyStep
              icon={<ShieldCheck className="h-4 w-4 text-bronze-400" strokeWidth={1.75} />}
              label="Legacy Member"
              sublabel="Build for generations"
              ringClass="bg-navy-800 ring-[2px] ring-bronze-600"
            />
          </div>

          <p className="mx-auto mt-10 max-w-[560px] text-center font-sans text-[14px] italic leading-relaxed text-charcoal-500">
            Many families begin in our Library, discover the Legacy Score, and
            begin building real protection with one of our strategists. Over time,
            the relationship deepens.
          </p>
        </section>

        {/* SECTION 5 — Final CTA */}
        <section className="bg-navy-900">
          <FiligreeFrame intensity="subtle" className="py-20 px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2
                className="font-display font-semibold text-cream-50"
                style={{ fontSize: "clamp(32px,4vw,44px)", lineHeight: 1.15 }}
              >
                Wherever you are in your journey,{" "}
                <em className="italic font-normal text-bronze-400">start here.</em>
              </h2>
              <p className="mx-auto mt-6 max-w-[480px] font-sans text-[15px] leading-[1.65] text-cream-50/80">
                Two paths in. The same destination — clarity for your family's
                future.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <ConsultCTA />
                <SecondaryButton asChild size="lg">
                  <Link to="/store">Visit the Store →</Link>
                </SecondaryButton>
              </div>
            </div>
          </FiligreeFrame>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- local presentational helpers ---------- */

function TierCard({
  children,
  topBorder,
  className,
}: {
  children: ReactNode;
  topBorder: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-[4px] bg-white p-8",
        topBorder,
        className,
      )}
    >
      {children}
    </div>
  );
}

function TierName({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3
      className={cn(
        "mt-5 text-center font-display text-[22px] text-navy-800",
        className,
      )}
    >
      {children}
    </h3>
  );
}

function TierTag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        "mt-2 text-center font-sans text-[12px] italic leading-relaxed",
        className,
      )}
    >
      {children}
    </p>
  );
}

function Divider() {
  return <hr className="my-4 border-0 border-t border-bronze-400/40" />;
}

function AccessList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start gap-2 font-sans text-[12px] leading-[1.55] text-charcoal-900"
        >
          <Check className="mt-[2px] h-3.5 w-3.5 shrink-0 text-bronze-600" strokeWidth={2.5} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SealRing({ size, children }: { size: number; children: ReactNode }) {
  return (
    <div className="mx-auto flex items-center justify-center">
      <div
        className="flex items-center justify-center rounded-full border-2 border-bronze-400 bg-transparent"
        style={{ width: size, height: size }}
      >
        {children}
      </div>
    </div>
  );
}

function SealFilled({
  size,
  fill,
  children,
}: {
  size: number;
  fill: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto flex items-center justify-center">
      <div
        className={cn("flex items-center justify-center rounded-full", fill)}
        style={{ width: size, height: size }}
      >
        {children}
      </div>
    </div>
  );
}

function SealLegacy({ size, children }: { size: number; children: ReactNode }) {
  return (
    <div className="mx-auto flex items-center justify-center">
      <div
        className="flex items-center justify-center rounded-full bg-navy-800 ring-[3px] ring-bronze-600"
        style={{
          width: size,
          height: size,
          boxShadow: "inset 0 0 0 1px var(--color-cream-50, #F5F1E8)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function QualifierCard({
  title,
  subtitle,
  compact,
}: {
  title: string;
  subtitle: string;
  compact?: boolean;
}) {
  return (
    <div className="rounded-[4px] border-t-2 border-t-bronze-600 bg-cream-50 p-5 text-center">
      <div
        className={cn(
          "font-display text-bronze-600",
          compact
            ? "text-[14px] font-semibold leading-[1.1]"
            : "text-[28px] font-bold leading-none",
        )}
      >
        {title}
      </div>
      <div className="mt-2 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-charcoal-500">
        {subtitle}
      </div>
    </div>
  );
}

function JourneyStep({
  icon,
  label,
  sublabel,
  ringClass,
}: {
  icon: ReactNode;
  label: string;
  sublabel: string;
  ringClass: string;
}) {
  return (
    <div className="flex flex-col items-center text-center">
      <div
        className={cn(
          "flex items-center justify-center rounded-full",
          ringClass,
        )}
        style={{ width: 40, height: 40 }}
      >
        {icon}
      </div>
      <div className="mt-3 font-display text-[14px] font-semibold text-navy-800">
        {label}
      </div>
      <div className="mt-1 font-sans text-[11px] text-charcoal-500">
        {sublabel}
      </div>
    </div>
  );
}

function JourneyArrow() {
  return (
    <ChevronRight
      className="hidden h-5 w-5 shrink-0 text-bronze-600 md:block"
      strokeWidth={1.75}
      aria-hidden
    />
  );
}

export default MembershipPage;